import React, { useState, useEffect } from 'react';
import Layout from '../../components/Layout';
import { services } from '../../utils/demoData';
import { db } from '../../services/firebase';
import { collection, addDoc, getDocs, query, where, orderBy, doc, updateDoc, deleteDoc, onSnapshot } from 'firebase/firestore';
import { useAuth } from '../../contexts/AuthContext';
import { useNotifications } from '../../contexts/NotificationContext';
import ReviewButton from '../../components/ReviewButton';
import { getAppointmentColors, getStatusColors, getServiceIcon } from '../../utils/appointmentColors';
// Importar la solución DINÁMICA para notificaciones de administradores

const Appointments = () => {
  const { currentUser } = useAuth();
  const { createAppointmentNotification, getAdminUserIds, getAllPossibleAdminIds, sendGuaranteedAdminNotification } = useNotifications();

  const [showBookingModal, setShowBookingModal] = useState(false);
  const [currentStep, setCurrentStep] = useState(1);
  const [selectedService, setSelectedService] = useState(null);
  const [selectedDate, setSelectedDate] = useState('');
  const [selectedSlot, setSelectedSlot] = useState('');
  const [notes, setNotes] = useState('');
  const [existingAppointments, setExistingAppointments] = useState([]);
  const [bookingSuccess, setBookingSuccess] = useState(false);
  const [updateSuccess, setUpdateSuccess] = useState(false);
  const [cancelSuccess, setCancelSuccess] = useState(false);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [appointmentToCancel, setAppointmentToCancel] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [editingAppointment, setEditingAppointment] = useState(null);
  const [updateError, setUpdateError] = useState('');
  const [viewingAppointment, setViewingAppointment] = useState(null);
  const [showViewModal, setShowViewModal] = useState(false);

  const [availableSlots, setAvailableSlots] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showStatusAlert, setShowStatusAlert] = useState(false);
  const [statusAlertMessage, setStatusAlertMessage] = useState('');
  const [statusAlertType, setStatusAlertType] = useState('success');

  // Load user's appointments from Firebase
  useEffect(() => {
    console.log('useEffect triggered for loadAppointments, currentUser:', currentUser?.uid);
    if (currentUser) {
      loadAppointments();
    }
  }, [currentUser]);

  // Real-time listener for appointment status changes
  useEffect(() => {
    if (!currentUser) return;

    const appointmentsRef = collection(db, 'appointments');
    const q = query(appointmentsRef, where('userId', '==', currentUser.uid));
    
    const unsubscribe = onSnapshot(q, (snapshot) => {
      console.log('Real-time listener triggered, changes:', snapshot.docChanges().length);
      
      snapshot.docChanges().forEach((change) => {
        const appointment = change.doc.data();
        const appointmentId = change.doc.id;
        
        console.log('Change type:', change.type, 'Appointment ID:', appointmentId);
        
        if (change.type === 'added') {
          console.log('New appointment added:', appointment);
          // Add new appointment to the list
          setExistingAppointments(prev => {
            const exists = prev.find(apt => apt.id === appointmentId);
            if (!exists) {
              const newAppointment = {
                id: appointmentId,
                ...appointment,
                createdAt: appointment.createdAt?.toDate ? appointment.createdAt.toDate() : appointment.createdAt,
                updatedAt: appointment.updatedAt?.toDate ? appointment.updatedAt.toDate() : appointment.updatedAt
              };
              return [newAppointment, ...prev];
            }
            return prev;
          });
        } else if (change.type === 'modified') {
          console.log('Appointment modified:', appointment);
          // Check if status changed
          const oldAppointment = existingAppointments.find(apt => apt.id === appointmentId);
          if (oldAppointment && oldAppointment.status !== appointment.status) {
            // Show alert based on new status
            if (appointment.status === 'confirmed') {
              setStatusAlertMessage(`✅ Great news! Your ${appointment.service} appointment has been confirmed!`);
              setStatusAlertType('success');
              setShowStatusAlert(true);
            } else if (appointment.status === 'cancelled' || appointment.status === 'rejected') {
              setStatusAlertMessage(`❌ Your ${appointment.service} appointment has been cancelled by the admin.`);
              setStatusAlertType('error');
              setShowStatusAlert(true);
            } else if (appointment.status === 'completed') {
              setStatusAlertMessage(`✓ Your ${appointment.service} appointment has been marked as completed!`);
              setStatusAlertType('success');
              setShowStatusAlert(true);
            }
            
            setTimeout(() => {
              setShowStatusAlert(false);
            }, 5000);
          }
          
          // Update local state
          setExistingAppointments(prev => 
            prev.map(apt => apt.id === appointmentId ? { ...apt, ...appointment, id: appointmentId } : apt)
          );
        }
      });
    });

    return () => unsubscribe();
  }, [currentUser, existingAppointments]);

  const loadAppointments = async () => {
    if (!currentUser) {
      console.log('No current user, skipping appointment load');
      return;
    }
    
    try {
      console.log('Loading appointments for user:', currentUser.uid);
      
      // Load from Firebase only
      const appointmentsRef = collection(db, 'appointments');
      const q = query(appointmentsRef, where('userId', '==', currentUser.uid));
      console.log('Firebase query created:', q);
      
      const querySnapshot = await getDocs(q);
      console.log('Query snapshot received, size:', querySnapshot.size);
      
      const appointments = [];
      querySnapshot.forEach((doc) => {
        const data = doc.data();
        console.log('Processing appointment:', doc.id, data);
        appointments.push({
          id: doc.id,
          ...data,
          // Convert Firestore Timestamp to Date if needed
          createdAt: data.createdAt?.toDate ? data.createdAt.toDate() : data.createdAt,
          updatedAt: data.updatedAt?.toDate ? data.updatedAt.toDate() : data.updatedAt
        });
      });
      
      // Sort by creation date (newest first)
      appointments.sort((a, b) => {
        const dateA = a.createdAt instanceof Date ? a.createdAt : new Date(a.createdAt);
        const dateB = b.createdAt instanceof Date ? b.createdAt : new Date(b.createdAt);
        return dateB - dateA;
      });
      
      console.log('Loaded Firebase appointments:', appointments.length, appointments);
      setExistingAppointments(appointments);
    } catch (error) {
      console.error('Error loading appointments:', error);
      setExistingAppointments([]);
    }
  };

  // Load available slots when date changes or appointments change
  useEffect(() => {
    loadAvailableSlots();
  }, [selectedDate, existingAppointments]);

  const loadAvailableSlots = async () => {
    if (!selectedDate) {
      setAvailableSlots([]);
      return;
    }

    setLoading(true);
    
    // Base slots for the day
    const baseSlots = [
      { id: '09:00', time: '9:00 AM', available: true },
      { id: '10:00', time: '10:00 AM', available: true },
      { id: '11:00', time: '11:00 AM', available: true },
      { id: '12:00', time: '12:00 PM', available: true },
      { id: '13:00', time: '1:00 PM', available: true },
      { id: '14:00', time: '2:00 PM', available: true },
      { id: '15:00', time: '3:00 PM', available: true },
      { id: '16:00', time: '4:00 PM', available: true },
      { id: '17:00', time: '5:00 PM', available: true }
    ];

    // Check for existing appointments on this date
    const appointmentsOnDate = existingAppointments.filter(apt => apt.date === selectedDate);
    
    // Mark slots as unavailable if they're already booked
    const updatedSlots = baseSlots.map(slot => {
      const isBooked = appointmentsOnDate.some(apt => apt.time === slot.time);
      
      if (isBooked && slot.available) {
        return {
          ...slot,
          available: false,
          reason: 'Already Booked'
        };
      }
      
      return slot;
    });

    // Simulate API delay
    setTimeout(() => {
      setAvailableSlots(updatedSlots);
      setLoading(false);
    }, 500);
  };

  const handleServiceSelect = (service) => {
    setSelectedService(service);
  };

  const handleNextStep = () => {
    if (currentStep < 3) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handlePrevStep = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleSubmit = async () => {
    if (!currentUser) {
      setUpdateError('Please log in to book an appointment.');
      return;
    }

    setIsSubmitting(true);
    setUpdateError(''); // Clear any previous errors
    
    try {
      const selectedSlotData = availableSlots.find(slot => slot.id === selectedSlot);
      
      // Create new appointment object for Firebase
      const appointmentData = {
        userId: currentUser.uid,
        userEmail: currentUser.email,
        userName: currentUser.displayName || currentUser.email,
        service: selectedService.name,
        date: selectedDate,
        time: selectedSlotData?.time,
        notes: notes,
        status: 'pending',
        createdAt: new Date(),
        updatedAt: new Date()
      };
      
      console.log('Attempting to save appointment:', appointmentData);
      
      // Save to Firebase only
      const docRef = await addDoc(collection(db, 'appointments'), appointmentData);
      console.log('Appointment saved to Firebase with ID:', docRef.id);
      
      const newAppointment = {
        id: docRef.id,
        ...appointmentData
      };
      
      setExistingAppointments([newAppointment, ...existingAppointments]);
      
        // Send notification to all admins - WORKING LIKE ESTIMATES AND TICKETS
        try {
          console.log('🔔 REAL APPOINTMENT: Sending notification to admins...');
          console.log('📅 Appointment data:', newAppointment);
          
          // Use the same method that works for estimates and tickets
          const adminUserIds = await getAdminUserIds();
          console.log('👥 Admin IDs found via getAdminUserIds:', adminUserIds);
          
          if (adminUserIds.length > 0) {
            console.log('📤 Sending appointment notifications to admins...');
            let successCount = 0;
            
            for (const adminId of adminUserIds) {
              console.log(`📤 Sending appointment notification to admin: ${adminId}`);
              try {
                await createAppointmentNotification(newAppointment, 'appointment_booked', adminId);
                console.log(`✅ Appointment notification sent to admin: ${adminId}`);
                successCount++;
              } catch (notifError) {
                console.error(`❌ Error sending appointment notification to admin ${adminId}:`, notifError);
              }
            }
            
            console.log(`🎉 Appointment notification delivery summary: ${successCount} successful`);
            
            if (successCount > 0) {
              console.log('✅ At least one admin received the appointment notification');
            } else {
              console.error('❌ No admins received the appointment notification');
            }
          } else {
            console.error('❌ No admin IDs found via getAdminUserIds');
            
            // Fallback: try to get admin IDs using the emergency method
            console.log('⚠️ Trying emergency admin search...');
            const emergencyAdminIds = await getAllPossibleAdminIds();
            console.log('🚨 Emergency admin IDs found:', emergencyAdminIds);
            
            if (emergencyAdminIds.length > 0) {
              console.log('📤 Sending appointment notifications to emergency admins...');
              for (const adminId of emergencyAdminIds) {
                try {
                  await createAppointmentNotification(newAppointment, 'appointment_booked', adminId);
                  console.log(`✅ Emergency appointment notification sent to admin: ${adminId}`);
                } catch (notifError) {
                  console.error(`❌ Error sending emergency appointment notification to admin ${adminId}:`, notifError);
                }
              }
            }
          }
        } catch (notificationError) {
          console.error('❌ Critical error in appointment notification system:', notificationError);
          console.error('Error details:', notificationError.message, notificationError.code);
        }
      
      // Show success state
      setBookingSuccess(true);
      
      // Auto close modal and reset after 3 seconds
      setTimeout(() => {
        setBookingSuccess(false);
        closeBookingModal(); // Close the modal completely
      }, 3000);
      
    } catch (error) {
      console.error('Booking failed:', error);
      console.error('Error details:', error.message);
      console.error('Error code:', error.code);
      setUpdateError(`Sorry, there was an error booking your appointment: ${error.message}`);
    } finally {
      setIsSubmitting(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'confirmed': return '#4caf50';
      case 'pending': return '#ff9800';
      case 'cancelled': return '#f44336';
      default: return '#666';
    }
  };

  const getStatusBadge = (status) => {
    const statusClass = 
      status === 'confirmed' ? 'status-success-modern' :
      status === 'pending' ? 'status-warning-modern' :
      status === 'completed' ? 'status-info-modern' :
      status === 'cancelled' || status === 'rejected' ? 'status-error-modern' :
      'status-pending-modern';

    return (
      <span className={`status-badge-modern ${statusClass}`}>
        {status}
      </span>
    );
  };

  const handleDeleteAppointment = (appointmentId) => {
    const appointment = existingAppointments.find(apt => apt.id === appointmentId);
    setAppointmentToCancel(appointment);
    setShowCancelModal(true);
  };

  const confirmCancelAppointment = async () => {
    if (appointmentToCancel) {
      try {
        // Update appointment status to 'cancelled' instead of deleting
        const appointmentRef = doc(db, 'appointments', appointmentToCancel.id);
        await updateDoc(appointmentRef, {
          status: 'cancelled',
          updatedAt: new Date()
        });
        
        console.log('Appointment cancelled successfully');
        
        // Send notification to all admins - SAME METHOD AS ESTIMATES
        try {
          const adminUserIds = await getAdminUserIds();
          console.log('👥 Admin IDs found for cancellation:', adminUserIds);
          
          for (const adminId of adminUserIds) {
            await createAppointmentNotification(appointmentToCancel, 'appointment_cancelled', adminId);
          }
          console.log('Cancellation notifications sent to admins');
        } catch (notificationError) {
          console.error('Error sending cancellation notifications to admins:', notificationError);
        }
        
        // Close modal and show success
        setShowCancelModal(false);
        setCancelSuccess(true);
        
        // Auto hide success after 3 seconds
        setTimeout(() => {
          setCancelSuccess(false);
          setAppointmentToCancel(null);
        }, 3000);
      } catch (error) {
        console.error('Error cancelling appointment:', error);
        setShowCancelModal(false);
        setUpdateError('Sorry, there was an error cancelling your appointment. Please try again.');
      }
    }
  };

  const closeCancelModal = () => {
    setShowCancelModal(false);
    setAppointmentToCancel(null);
  };

  const canEditOrDelete = (status) => {
    return status === 'pending';
  };

  const handleEditAppointment = async (appointment) => {
    console.log('Editing appointment:', appointment);
    
    // Populate form with existing appointment data
    const service = services.find(s => s.name === appointment.service);
    setSelectedService(service);
    setSelectedDate(appointment.date);
    setNotes(appointment.notes || '');
    
    // Set editing mode and go to step 3 (final step)
    setEditingAppointment(appointment);
    setShowBookingModal(true);
    setCurrentStep(3);
    
    // Load slots for the selected date and then set the slot
    setLoading(true);
    
    // Generate slots for the appointment date
    const baseSlots = [
      { id: '09:00', time: '9:00 AM', available: true },
      { id: '10:00', time: '10:00 AM', available: true },
      { id: '11:00', time: '11:00 AM', available: true },
      { id: '12:00', time: '12:00 PM', available: true },
      { id: '13:00', time: '1:00 PM', available: true },
      { id: '14:00', time: '2:00 PM', available: true },
      { id: '15:00', time: '3:00 PM', available: true },
      { id: '16:00', time: '4:00 PM', available: true },
      { id: '17:00', time: '5:00 PM', available: true }
    ];

    // Check for existing appointments on this date (excluding the current appointment being edited)
    const appointmentsOnDate = existingAppointments.filter(apt => 
      apt.date === appointment.date && apt.id !== appointment.id
    );
    
    // Mark slots as unavailable if they're already booked
    const updatedSlots = baseSlots.map(slot => {
      const isBooked = appointmentsOnDate.some(apt => apt.time === slot.time);
      
      if (isBooked && slot.available) {
        return {
          ...slot,
          available: false,
          reason: 'Already Booked'
        };
      }
      
      return slot;
    });

    setAvailableSlots(updatedSlots);
    
    // Find and set the current appointment's time slot
    const timeSlot = updatedSlots.find(slot => slot.time === appointment.time);
    if (timeSlot) {
      setSelectedSlot(timeSlot.id);
      console.log('Set selected slot:', timeSlot.id, 'for time:', appointment.time);
    } else {
      console.warn('Could not find slot for time:', appointment.time);
    }
    
    setLoading(false);
  };

  const handleUpdateAppointment = async () => {
    console.log('Update validation:', {
      selectedService: selectedService,
      selectedDate: selectedDate,
      selectedSlot: selectedSlot,
      editingAppointment: editingAppointment
    });
    
    if (!selectedService || !selectedDate || !selectedSlot) {
      const missingFields = [];
      if (!selectedService) missingFields.push('service');
      if (!selectedDate) missingFields.push('date');
      if (!selectedSlot) missingFields.push('time slot');
      
      setUpdateError(`Please fill in all required fields. Missing: ${missingFields.join(', ')}`);
      return;
    }

    setIsSubmitting(true);
    setUpdateError('');
    
    try {
      const selectedSlotData = availableSlots.find(slot => slot.id === selectedSlot);
      
      // Update appointment in Firebase
      const appointmentRef = doc(db, 'appointments', editingAppointment.id);
      const updateData = {
        service: selectedService.name,
        date: selectedDate,
        time: selectedSlotData?.time,
        notes: notes || '',
        updatedAt: new Date()
      };
      
      await updateDoc(appointmentRef, updateData);
      
      // Update in local state
      const updatedAppointment = {
        ...editingAppointment,
        ...updateData
      };
      
      const updatedAppointments = existingAppointments.map(apt => 
        apt.id === editingAppointment.id ? updatedAppointment : apt
      );
      setExistingAppointments(updatedAppointments);
      
      // Show update success state
      setUpdateSuccess(true);
      
      // Auto redirect after 3 seconds
      setTimeout(() => {
        setUpdateSuccess(false);
        setEditingAppointment(null);
        setCurrentStep(1);
        setSelectedService(null);
        setSelectedDate('');
        setSelectedSlot('');
        setNotes('');
        setUpdateError('');
      }, 3000);
      
    } catch (error) {
      console.error('Update failed:', error);
      setUpdateError('Sorry, there was an error updating your appointment. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancelEdit = () => {
    setEditingAppointment(null);
    setCurrentStep(1);
    setSelectedService(null);
    setSelectedDate('');
    setSelectedSlot('');
    setNotes('');
    setUpdateError('');
  };

  const startBooking = () => {
    setShowBookingModal(true);
    setCurrentStep(1);
    setSelectedService(null);
    setSelectedDate('');
    setSelectedSlot('');
    setNotes('');
    setUpdateError('');
    setEditingAppointment(null);
  };

  const closeBookingModal = () => {
    setShowBookingModal(false);
    setCurrentStep(1);
    setSelectedService(null);
    setSelectedDate('');
    setSelectedSlot('');
    setNotes('');
    setUpdateError('');
    setEditingAppointment(null);
  };

  const handleViewAppointment = (appointment) => {
    setViewingAppointment(appointment);
    setShowViewModal(true);
  };

  const closeViewModal = () => {
    setShowViewModal(false);
    setViewingAppointment(null);
  };

  return (
    <Layout>
      {/* Status Change Alert */}
      {showStatusAlert && (
        <div style={{
          position: 'fixed',
          top: '80px',
          right: '20px',
          zIndex: 9999,
          background: statusAlertType === 'success' ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' :
                      'linear-gradient(135deg, #fa709a 0%, #fee140 100%)',
          color: 'white',
          padding: '1rem 1.5rem',
          borderRadius: '12px',
          boxShadow: '0 10px 30px rgba(0, 0, 0, 0.2)',
          display: 'flex',
          alignItems: 'center',
          gap: '1rem',
          minWidth: '300px',
          maxWidth: '500px',
          animation: 'slideInRight 0.3s ease-out',
          fontSize: '0.95rem',
          fontWeight: '500'
        }}>
          <div style={{ fontSize: '1.5rem' }}>
            {statusAlertType === 'success' ? '✓' : '✕'}
          </div>
          <div style={{ flex: 1 }}>{statusAlertMessage}</div>
          <button
            onClick={() => setShowStatusAlert(false)}
            style={{
              background: 'rgba(255, 255, 255, 0.2)',
              border: 'none',
              color: 'white',
              width: '24px',
              height: '24px',
              borderRadius: '50%',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '1rem',
              fontWeight: 'bold',
              transition: 'all 0.2s ease'
            }}
            onMouseEnter={(e) => e.target.style.background = 'rgba(255, 255, 255, 0.3)'}
            onMouseLeave={(e) => e.target.style.background = 'rgba(255, 255, 255, 0.2)'}
          >
            ×
          </button>
        </div>
      )}

      <div style={{
        padding: window.innerWidth <= 640 ? '0.5rem' : window.innerWidth <= 768 ? '1rem' : '1.5rem',
        maxWidth: '100%',
        overflowX: 'hidden',
        boxSizing: 'border-box'
      }}>
        {/* Modern Page Header */}
        <div className="page-header">
          <div className="page-header-content" style={{
            flexDirection: window.innerWidth <= 768 ? 'column' : 'row',
            alignItems: window.innerWidth <= 768 ? 'stretch' : 'flex-start',
            gap: window.innerWidth <= 768 ? 'var(--space-4)' : 'var(--space-6)'
          }}>
            <div className="page-title-section">
              <h1 className="page-title" style={{
                fontSize: window.innerWidth <= 640 ? '1.5rem' : '2.5rem'
              }}>
                My Appointments
              </h1>
              <p className="page-subtitle" style={{
                fontSize: window.innerWidth <= 640 ? '0.8rem' : '1.125rem'
              }}>
                Schedule and manage your tree service appointments
              </p>
            </div>
            <button 
              className={`btn btn-primary ${window.innerWidth <= 768 ? 'mobile-full-width' : 'btn-lg'}`}
              onClick={startBooking}
              style={{
                width: window.innerWidth <= 768 ? '100%' : 'auto',
                justifyContent: 'center'
              }}
            >
              <img 
                src="/appointment.jpg" 
                alt="Book Appointment" 
                style={{ 
                  width: '28px', 
                  height: '28px', 
                  objectFit: 'contain',
                  marginRight: '8px'
                }} 
              />
              Book Appointment
            </button>
          </div>
        </div>

        {/* Existing Appointments */}
        <div className="modern-card">
          <div className="modern-card-header">
            <h3 className="modern-card-title">
              <img 
                src="/appointment.jpg" 
                alt="Your Appointments" 
                className="modern-card-icon"
                style={{ 
                  width: '32px', 
                  height: '32px', 
                  objectFit: 'contain'
                }} 
              />
              Your Appointments
            </h3>
            <p className="modern-card-subtitle">
              {existingAppointments.length > 0 ? `${existingAppointments.length} appointment${existingAppointments.length > 1 ? 's' : ''} found` : 'No appointments scheduled'}
            </p>
          </div>
          
          {existingAppointments.length > 0 ? (
            <div className="modern-grid">
              {existingAppointments.map((appointment, index) => (
                <div key={appointment.id} className="modern-estimate-card animate-scale" style={{
                  animationDelay: `${index * 0.1}s`
                }}>
                  <div className="modern-estimate-header">
                    <div className="modern-estimate-service">
                      <div
                        style={{
                          width: '8px',
                          alignSelf: 'stretch',
                          borderRadius: '4px',
                          backgroundColor: getAppointmentColors(appointment.service).borderColor,
                          marginRight: '12px'
                        }}
                      ></div>
                      <div>
                        <h4 
                          className="modern-estimate-title"
                          style={{ color: getAppointmentColors(appointment.service).primary }}
                        >
                          {appointment.service}
                        </h4>
                        <p className="modern-estimate-date">{appointment.date} at {appointment.time}</p>
                      </div>
                    </div>
                    
                    <div className="modern-estimate-status">
                      <span 
                        className="modern-status-badge"
                        style={{
                          backgroundColor: appointment.status === 'confirmed' ? getAppointmentColors(appointment.service).secondary :
                                        appointment.status === 'pending' ? '#fef3c7' :
                                        appointment.status === 'completed' ? getAppointmentColors(appointment.service).secondary :
                                        appointment.status === 'cancelled' ? '#fee2e2' :
                                        '#f3f4f6',
                          color: appointment.status === 'confirmed' ? getAppointmentColors(appointment.service).accent :
                                 appointment.status === 'pending' ? '#d97706' :
                                 appointment.status === 'completed' ? getAppointmentColors(appointment.service).accent :
                                 appointment.status === 'cancelled' ? '#dc2626' :
                                 '#6b7280',
                          border: `1px solid ${appointment.status === 'confirmed' ? getAppointmentColors(appointment.service).primary + '40' :
                                           appointment.status === 'pending' ? '#f59e0b40' :
                                           appointment.status === 'completed' ? getAppointmentColors(appointment.service).primary + '40' :
                                           appointment.status === 'cancelled' ? '#ef444440' :
                                           '#6b728040'}`
                        }}
                      >
                        {appointment.status === 'confirmed' ? '✓ Confirmed' :
                         appointment.status === 'pending' ? '⏳ Pending' :
                         appointment.status === 'completed' ? '✓ Completed' :
                         appointment.status === 'cancelled' ? '✗ Cancelled' :
                         appointment.status === 'rejected' ? '✗ Rejected' :
                         appointment.status}
                      </span>
                    </div>
                  </div>

                  <div className="modern-estimate-content">
                    <p className="modern-estimate-description">
                      {appointment.notes || 'No additional notes provided'}
                    </p>
                  </div>

                  <div className="modern-estimate-actions">
                    <button 
                      className="btn btn-primary btn-sm"
                      onClick={() => handleViewAppointment(appointment)}
                    >
                      <span>◐</span>
                      View Details
                    </button>
                    {canEditOrDelete(appointment.status) && (
                      <>
                        <button 
                          className="btn btn-secondary btn-sm"
                          onClick={() => handleEditAppointment(appointment)}
                        >
                          <span>□</span>
                          Edit
                        </button>
                        <button 
                          className="btn btn-danger btn-sm"
                          onClick={() => handleDeleteAppointment(appointment.id)}
                        >
                          <span>❌</span>
                          Cancel
                        </button>
                      </>
                    )}
                    {appointment.status === 'completed' && (
                      <ReviewButton 
                        appointmentId={appointment.id} 
                        appointmentService={appointment.service} 
                      />
                    )}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="modern-empty-state">
              <div className="modern-empty-icon">📅</div>
              <h4 className="modern-empty-title">No appointments scheduled</h4>
              <p className="modern-empty-description">
                Ready to get started? Book your first tree service appointment and let our experts take care of your trees.
              </p>
              <button 
                className="btn btn-primary btn-lg"
                onClick={startBooking}
              >
                <img 
                  src="/appointment.jpg" 
                  alt="Schedule Appointment" 
                  style={{ 
                    width: '20px', 
                    height: '20px', 
                    objectFit: 'contain',
                    marginRight: '8px'
                  }} 
                />
                Schedule Your First Appointment
              </button>
            </div>
          )}
        </div>

        {/* Booking Modal */}
        {(showBookingModal || editingAppointment) && !bookingSuccess && !updateSuccess && (
          <div 
            className="modern-modal-overlay"
            onClick={() => {
              setShowBookingModal(false);
              setEditingAppointment(null);
              resetBookingForm();
            }}
          >
            <div 
              className="modern-modal"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="modern-modal-header">
                <h2 className="modern-modal-title">
                  <span className="modern-modal-icon">📅</span>
                  {editingAppointment ? 'Edit Appointment' : 'Book New Appointment'}
                </h2>
                <button 
                  className="modern-modal-close"
                  onClick={editingAppointment ? handleCancelEdit : closeBookingModal}
                >
                  ✕
                </button>
              </div>
              
              <div className="modern-modal-body">
                {/* Modern Progress Steps */}
                <div className="modern-progress-steps">
                  {[
                    { step: 1, title: 'Service' },
                    { step: 2, title: 'Schedule' },
                    { step: 3, title: 'Confirm' }
                  ].map((item, index) => (
                    <React.Fragment key={item.step}>
                      <div className={`modern-progress-step ${currentStep >= item.step ? 'active' : ''}`}>
                        <div className="modern-progress-step-circle">
                          {currentStep > item.step ? '✓' : item.step}
                        </div>
                        <div className="modern-progress-step-label">
                          <span className="modern-progress-step-number">Step {item.step}</span>
                          <span className="modern-progress-step-title">{item.title}</span>
                        </div>
                      </div>
                      {index < 2 && (
                        <div className={`modern-progress-connector ${currentStep > item.step ? 'completed' : ''}`} />
                      )}
                    </React.Fragment>
                  ))}
                </div>

                {/* Step 1: Select Service */}
                {currentStep === 1 && (
                  <div className="modern-form-section">
                    <h3 className="modern-form-section-title">
                      🌳 Choose Your Service
                    </h3>
                    <div className="modern-service-grid">
                      {services.map((service) => {
                        const colors = getAppointmentColors(service.name);
                        const isSelected = selectedService?.name === service.name;
                        return (
                          <div
                            key={service.name}
                            className={`modern-service-card ${isSelected ? 'selected' : ''}`}
                            onClick={() => handleServiceSelect(service)}
                            style={{
                              border: isSelected ? `3px solid ${colors.borderColor}` : '2px solid #e5e7eb',
                              borderLeft: isSelected ? `8px solid ${colors.borderColor}` : '2px solid #e5e7eb',
                              borderRadius: '8px',
                              backgroundColor: isSelected ? colors.secondary : '#ffffff',
                              transform: isSelected ? 'translateY(-2px)' : 'translateY(0)',
                              boxShadow: isSelected ? `0 8px 25px ${colors.primary}30` : '0 4px 12px rgba(0,0,0,0.1)',
                              transition: 'all 0.3s ease',
                              padding: '20px',
                              position: 'relative'
                            }}
                          >
                            <h4 
                              className="modern-service-title"
                              style={{ 
                                color: isSelected ? colors.primary : '#1f2937',
                                fontWeight: '600'
                              }}
                            >
                              {service.name}
                            </h4>
                            <p 
                              className="modern-service-description"
                              style={{ 
                                color: isSelected ? colors.accent : '#6b7280'
                              }}
                            >
                              {service.description}
                            </p>
                            {isSelected && (
                              <div 
                                style={{
                                  position: 'absolute',
                                  top: '12px',
                                  right: '12px',
                                  backgroundColor: colors.primary,
                                  color: 'white',
                                  borderRadius: '50%',
                                  width: '24px',
                                  height: '24px',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  fontSize: '12px',
                                  fontWeight: 'bold'
                                }}
                              >
                                ✓
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 'var(--space-6)' }}>
                      <button
                        className="btn btn-primary btn-lg"
                        onClick={handleNextStep}
                        disabled={!selectedService}
                        style={{ 
                          opacity: selectedService ? 1 : 0.5,
                          backgroundColor: selectedService ? getAppointmentColors(selectedService.name).primary : '#6b7280',
                          borderColor: selectedService ? getAppointmentColors(selectedService.name).primary : '#6b7280',
                          boxShadow: selectedService ? `0 4px 12px ${getAppointmentColors(selectedService.name).primary}40` : 'none',
                          transform: selectedService ? 'translateY(-1px)' : 'translateY(0)',
                          transition: 'all 0.3s ease'
                        }}
                      >
                        <span>⊡</span>
                        Next: Select Date & Time
                      </button>
                    </div>
                  </div>
                )}

                {/* Step 2: Select Date & Time */}
                {currentStep === 2 && (
                  <div className="modern-form-section">
                    <div style={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      gap: '12px', 
                      marginBottom: '24px',
                      padding: '16px',
                      backgroundColor: getAppointmentColors(selectedService?.name).secondary,
                      borderRadius: '12px',
                      border: `1px solid ${getAppointmentColors(selectedService?.name).primary}40`
                    }}>
                      <div style={{
                        backgroundColor: getAppointmentColors(selectedService?.name).borderColor,
                        width: '8px',
                        height: '40px',
                        borderRadius: '4px',
                        marginRight: '12px'
                      }}>
                      </div>
                      <div>
                        <h3 style={{ 
                          margin: 0, 
                          color: getAppointmentColors(selectedService?.name).primary,
                          fontSize: '18px',
                          fontWeight: '600'
                        }}>
                          Schedule Your {selectedService?.name}
                        </h3>
                        <p style={{ 
                          margin: 0, 
                          color: getAppointmentColors(selectedService?.name).accent,
                          fontSize: '14px'
                        }}>
                          {selectedService?.description}
                        </p>
                      </div>
                    </div>
                    
                    <div className="modern-form-group">
                      <label className="modern-form-label">Select Date</label>
                      <input
                        type="date"
                        className="modern-form-input"
                        value={selectedDate}
                        onChange={(e) => {
                          setSelectedDate(e.target.value);
                          setSelectedSlot(''); // Reset slot when date changes
                        }}
                        min={new Date().toISOString().split('T')[0]}
                        style={{ maxWidth: '300px' }}
                      />
                    </div>

                    {selectedDate && (
                      <div className="modern-form-group">
                        <label className="modern-form-label">Available Time Slots</label>
                        {loading ? (
                          <div className="modern-loading-state" style={{ padding: 'var(--space-8)' }}>
                            <div className="modern-loading-spinner"></div>
                            <p>Loading available slots...</p>
                          </div>
                        ) : availableSlots.length > 0 ? (
                          <div className="modern-time-slots">
                            {availableSlots.map((slot) => {
                              const colors = getAppointmentColors(selectedService?.name);
                              const isSelected = selectedSlot === slot.id;
                              const isAvailable = slot.available;
                              
                              return (
                                <button
                                  key={slot.id}
                                  type="button"
                                  className={`modern-time-slot ${isSelected ? 'selected' : ''}`}
                                  onClick={() => isAvailable && setSelectedSlot(slot.id)}
                                  disabled={!isAvailable}
                                  title={!isAvailable ? slot.reason : ''}
                                  style={{
                                    border: isSelected ? `2px solid ${colors.primary}` : 
                                           isAvailable ? `1px solid #e5e7eb` : '1px solid #d1d5db',
                                    backgroundColor: isSelected ? colors.secondary : 
                                                   isAvailable ? '#ffffff' : '#f9fafb',
                                    color: isSelected ? colors.primary : 
                                          isAvailable ? '#374151' : '#9ca3af',
                                    transform: isSelected ? 'translateY(-1px)' : 'translateY(0)',
                                    boxShadow: isSelected ? `0 4px 12px ${colors.primary}30` : 
                                              isAvailable ? '0 2px 4px rgba(0,0,0,0.1)' : 'none',
                                    transition: 'all 0.2s ease'
                                  }}
                                >
                                  <div 
                                    className="modern-time-slot-time"
                                    style={{ 
                                      fontWeight: isSelected ? '600' : '500',
                                      color: isSelected ? colors.primary : 'inherit'
                                    }}
                                  >
                                    {slot.time}
                                  </div>
                                  {!isAvailable && (
                                    <div className="modern-time-slot-status">{slot.reason}</div>
                                  )}
                                  {isSelected && (
                                    <div style={{
                                      position: 'absolute',
                                      top: '8px',
                                      right: '8px',
                                      backgroundColor: colors.primary,
                                      color: 'white',
                                      borderRadius: '50%',
                                      width: '20px',
                                      height: '20px',
                                      display: 'flex',
                                      alignItems: 'center',
                                      justifyContent: 'center',
                                      fontSize: '10px',
                                      fontWeight: 'bold'
                                    }}>
                                      ✓
                                    </div>
                                  )}
                                </button>
                              );
                            })}
                          </div>
                        ) : (
                          <div className="modern-empty-state" style={{ padding: 'var(--space-8)' }}>
                            <div className="modern-empty-icon">⏰</div>
                            <h4 className="modern-empty-title">No slots available</h4>
                            <p className="modern-empty-description">
                              Please select another date to see available time slots.
                            </p>
                          </div>
                        )}
                      </div>
                    )}

                    <div style={{ display: 'flex', gap: 'var(--space-4)', justifyContent: 'space-between', marginTop: 'var(--space-6)' }}>
                      <button 
                        className="btn btn-secondary btn-lg" 
                        onClick={handlePrevStep}
                        style={{
                          backgroundColor: '#f3f4f6',
                          borderColor: '#d1d5db',
                          color: '#374151'
                        }}
                      >
                        <span>←</span>
                        Back
                      </button>
                      <button
                        className="btn btn-primary btn-lg"
                        onClick={handleNextStep}
                        disabled={!selectedDate || !selectedSlot}
                        style={{ 
                          opacity: (selectedDate && selectedSlot) ? 1 : 0.5,
                          backgroundColor: (selectedDate && selectedSlot) ? getAppointmentColors(selectedService?.name).primary : '#6b7280',
                          borderColor: (selectedDate && selectedSlot) ? getAppointmentColors(selectedService?.name).primary : '#6b7280',
                          boxShadow: (selectedDate && selectedSlot) ? `0 4px 12px ${getAppointmentColors(selectedService?.name).primary}40` : 'none',
                          transform: (selectedDate && selectedSlot) ? 'translateY(-1px)' : 'translateY(0)',
                          transition: 'all 0.3s ease'
                        }}
                      >
                        <span>✅</span>
                        Next: Confirm Details
                      </button>
                    </div>
                  </div>
                )}

          {/* Step 3: Add Notes & Confirm */}
          {currentStep === 3 && (
            <div>
              <div style={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: '12px', 
                marginBottom: '24px',
                padding: '16px',
                backgroundColor: getAppointmentColors(selectedService?.name).secondary,
                borderRadius: '12px',
                border: `1px solid ${getAppointmentColors(selectedService?.name).primary}40`
              }}>
                <div style={{
                  backgroundColor: getAppointmentColors(selectedService?.name).primary,
                  color: 'white',
                  borderRadius: '50%',
                  width: '40px',
                  height: '40px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '16px',
                  fontWeight: 'bold'
                }}>
                  {getAppointmentColors(selectedService?.name).icon}
                </div>
                <div>
                  <h4 style={{ 
                    margin: 0, 
                    color: getAppointmentColors(selectedService?.name).primary,
                    fontSize: window.innerWidth <= 640 ? '1rem' : '1.125rem',
                    fontWeight: '600'
                  }}>
                    Add Details & Confirm
                  </h4>
                  <p style={{ 
                    margin: 0, 
                    color: getAppointmentColors(selectedService?.name).accent,
                    fontSize: '14px'
                  }}>
                    Review your appointment details and add any special requirements
                  </p>
                </div>
              </div>
              
              <div className="modern-form-group">
                <label className="modern-form-label">Additional Notes (Optional)</label>
                <textarea
                  className="modern-form-input"
                  rows="4"
                  placeholder="Any specific requirements or details about the service needed..."
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  style={{
                    resize: 'vertical',
                    minHeight: '100px',
                    fontFamily: 'inherit'
                  }}
                />
                <div className="modern-form-hint">
                  Provide any specific details about your tree service needs
                </div>
              </div>

              {/* Error Message */}
              {updateError && (
                <div style={{
                  background: '#ffebee',
                  border: '1px solid #f44336',
                  borderRadius: '8px',
                  padding: '1rem',
                  marginBottom: '1rem',
                  color: '#d32f2f'
                }}>
                  <strong>Error:</strong> {updateError}
                </div>
              )}

              {/* Booking Summary */}
              <div 
                className="card" 
                style={{ 
                  background: getAppointmentColors(selectedService?.name).secondary,
                  border: `1px solid ${getAppointmentColors(selectedService?.name).primary}40`,
                  borderRadius: '12px',
                  marginBottom: '2rem',
                  padding: '20px'
                }}
              >
                <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
                marginBottom: '16px'
              }}>
                <div style={{
                  backgroundColor: getAppointmentColors(selectedService?.name).borderColor,
                  width: '8px',
                  height: '36px',
                  borderRadius: '4px',
                  marginRight: '12px'
                }}>
                </div>
                  <h5 style={{ 
                    margin: 0, 
                    color: getAppointmentColors(selectedService?.name).primary,
                    fontSize: window.innerWidth <= 640 ? '1rem' : '1.125rem',
                    fontWeight: '600'
                  }}>
                    {editingAppointment ? 'Edit Summary' : 'Booking Summary'}
                  </h5>
                </div>
                <div style={{ display: 'grid', gap: '12px' }}>
                  <div style={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: '8px',
                    padding: '8px 12px',
                    backgroundColor: 'white',
                    borderRadius: '8px',
                    border: `1px solid ${getAppointmentColors(selectedService?.name).primary}20`
                  }}>
                    <span style={{ color: getAppointmentColors(selectedService?.name).primary, fontWeight: '600' }}>🌳</span>
                    <span><strong>Service:</strong> {selectedService?.name}</span>
                  </div>
                  <div style={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: '8px',
                    padding: '8px 12px',
                    backgroundColor: 'white',
                    borderRadius: '8px',
                    border: `1px solid ${getAppointmentColors(selectedService?.name).primary}20`
                  }}>
                    <span style={{ color: getAppointmentColors(selectedService?.name).primary, fontWeight: '600' }}>📅</span>
                    <span><strong>Date:</strong> {selectedDate}</span>
                  </div>
                  <div style={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: '8px',
                    padding: '8px 12px',
                    backgroundColor: 'white',
                    borderRadius: '8px',
                    border: `1px solid ${getAppointmentColors(selectedService?.name).primary}20`
                  }}>
                    <span style={{ color: getAppointmentColors(selectedService?.name).primary, fontWeight: '600' }}>⏰</span>
                    <span><strong>Time:</strong> {availableSlots.find(slot => slot.id === selectedSlot)?.time}</span>
                  </div>
                  {notes && (
                    <div style={{ 
                      display: 'flex', 
                      alignItems: 'flex-start', 
                      gap: '8px',
                      padding: '8px 12px',
                      backgroundColor: 'white',
                      borderRadius: '8px',
                      border: `1px solid ${getAppointmentColors(selectedService?.name).primary}20`
                    }}>
                      <span style={{ color: getAppointmentColors(selectedService?.name).primary, fontWeight: '600' }}>📝</span>
                      <span><strong>Notes:</strong> {notes}</span>
                    </div>
                  )}
                </div>
              </div>

              <div style={{ display: 'flex', gap: '1rem', justifyContent: 'space-between' }}>
                <button 
                  className="btn btn-secondary" 
                  onClick={editingAppointment ? handleCancelEdit : handlePrevStep}
                  style={{
                    backgroundColor: '#f3f4f6',
                    borderColor: '#d1d5db',
                    color: '#374151',
                    padding: '12px 24px',
                    borderRadius: '8px',
                    fontWeight: '500'
                  }}
                >
                  {editingAppointment ? 'Cancel Edit' : 'Back'}
                </button>
                <button 
                  className="btn btn-primary" 
                  onClick={editingAppointment ? handleUpdateAppointment : handleSubmit}
                  disabled={isSubmitting}
                  style={{ 
                    opacity: isSubmitting ? 0.7 : 1,
                    cursor: isSubmitting ? 'not-allowed' : 'pointer',
                    backgroundColor: getAppointmentColors(selectedService?.name).primary,
                    borderColor: getAppointmentColors(selectedService?.name).primary,
                    color: 'white',
                    padding: '12px 24px',
                    borderRadius: '8px',
                    fontWeight: '600',
                    boxShadow: `0 4px 12px ${getAppointmentColors(selectedService?.name).primary}40`,
                    transform: isSubmitting ? 'translateY(0)' : 'translateY(-1px)',
                    transition: 'all 0.3s ease'
                  }}
                >
                  {isSubmitting ? 
                    (editingAppointment ? 'Updating...' : 'Booking...') : 
                    (editingAppointment ? 'Update Appointment' : 'Book Appointment')
                  }
                </button>
              </div>
            </div>
          )}
              </div>
            </div>
          </div>
        )}

        {/* Booking Success Screen */}
        {bookingSuccess && (
            <div style={{ textAlign: 'center', padding: '3rem 1rem' }}>
              <div style={{ 
                fontSize: window.innerWidth <= 640 ? '3rem' : '4rem', 
                marginBottom: '1rem',
                animation: 'bounce 1s ease-in-out'
              }}>
                ✅
              </div>
              <h2 style={{ 
                color: '#2e7d32', 
                marginBottom: '1rem',
                fontSize: '2rem',
                fontWeight: 'bold'
              }}>
                Appointment Booked Successfully!
              </h2>
              <div className="card" style={{ 
                maxWidth: '500px', 
                margin: '0 auto 2rem auto',
                background: '#e8f5e8',
                border: '2px solid #4caf50'
              }}>
                <h4 style={{ marginBottom: '1rem', color: '#2e7d32' }}>Booking Details</h4>
                <div style={{ display: 'grid', gap: '0.75rem', textAlign: 'left' }}>
                  <div><strong>Service:</strong> {selectedService?.name}</div>
                  <div><strong>Date:</strong> {new Date(selectedDate).toLocaleDateString()}</div>
                  <div><strong>Time:</strong> {availableSlots.find(slot => slot.id === selectedSlot)?.time}</div>
                  {notes && <div><strong>Notes:</strong> {notes}</div>}
                  <div><strong>Status:</strong> <span style={{ color: '#ff9800', fontWeight: '600' }}>Pending Confirmation</span></div>
                </div>
              </div>
              <p style={{ color: '#666', marginBottom: '2rem' }}>
                We'll contact you soon to confirm your appointment details.
              </p>
              <div style={{ display: 'flex', gap: '1rem', justifyContent: 'center', marginBottom: '2rem' }}>
                <button 
                  className="btn btn-primary"
                  onClick={() => {
                    setBookingSuccess(false);
                    setCurrentStep(1);
                    setSelectedService(null);
                    setSelectedDate('');
                    setSelectedSlot('');
                    setNotes('');
                  }}
                >
                  View My Appointments
                </button>
                <button 
                  className="btn btn-secondary"
                  onClick={() => {
                    setBookingSuccess(false);
                    setCurrentStep(1);
                    setSelectedService(null);
                    setSelectedDate('');
                    setSelectedSlot('');
                    setNotes('');
                  }}
                >
                  Book Another
                </button>
              </div>
              <div style={{ 
                background: '#f0f0f0', 
                padding: '1rem', 
                borderRadius: '8px',
                color: '#666',
                fontSize: '0.875rem'
              }}>
                Auto-redirecting in a few seconds...
              </div>
            </div>
          )}

        {/* Update Success Screen */}
        {updateSuccess && (
          <div style={{ textAlign: 'center', padding: '3rem 1rem' }}>
            <div style={{ 
              fontSize: '4rem', 
              marginBottom: '1rem',
              animation: 'bounce 1s ease-in-out'
            }}>
              ✅
            </div>
            <h2 style={{ 
              color: '#2e7d32', 
              marginBottom: '1rem',
              fontSize: '2rem',
              fontWeight: 'bold'
            }}>
              Appointment Updated Successfully!
            </h2>
            <div className="card" style={{ 
              maxWidth: '500px', 
              margin: '0 auto 2rem auto',
              background: '#e8f5e8',
              border: '2px solid #4caf50'
            }}>
              <h4 style={{ marginBottom: '1rem', color: '#2e7d32' }}>Updated Details</h4>
              <div style={{ display: 'grid', gap: '0.75rem', textAlign: 'left' }}>
                <div><strong>Service:</strong> {selectedService?.name}</div>
                <div><strong>Date:</strong> {new Date(selectedDate).toLocaleDateString()}</div>
                <div><strong>Time:</strong> {availableSlots.find(slot => slot.id === selectedSlot)?.time}</div>
                {notes && <div><strong>Notes:</strong> {notes}</div>}
                <div><strong>Status:</strong> <span style={{ color: '#ff9800', fontWeight: '600' }}>Pending Confirmation</span></div>
              </div>
            </div>
            <p style={{ color: '#666', marginBottom: '2rem' }}>
              Your appointment has been updated successfully.
            </p>
            <div style={{ display: 'flex', gap: '1rem', justifyContent: 'center', marginBottom: '2rem' }}>
              <button 
                className="btn btn-primary"
                onClick={() => {
                  setUpdateSuccess(false);
                  setEditingAppointment(null);
                  setCurrentStep(1);
                  setSelectedService(null);
                  setSelectedDate('');
                  setSelectedSlot('');
                  setNotes('');
                }}
              >
                View My Appointments
              </button>
              <button 
                className="btn btn-secondary"
                onClick={() => {
                  setUpdateSuccess(false);
                  setEditingAppointment(null);
                  setCurrentStep(1);
                  setSelectedService(null);
                  setSelectedDate('');
                  setSelectedSlot('');
                  setNotes('');
                }}
              >
                Done
              </button>
            </div>
            <div style={{ 
              background: '#f0f0f0', 
              padding: '1rem', 
              borderRadius: '8px',
              color: '#666',
              fontSize: '0.875rem'
            }}>
              Returning to appointments in a few seconds...
            </div>
          </div>
        )}

        {/* Cancel Confirmation Modal */}
        {showCancelModal && (
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000
          }}>
            <div style={{
              background: 'white',
              borderRadius: '12px',
              padding: '2rem',
              maxWidth: '500px',
              width: '90%',
              boxShadow: '0 10px 25px rgba(0, 0, 0, 0.2)'
            }}>
              <div style={{ textAlign: 'center', marginBottom: '1.5rem' }}>
                <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>⚠️</div>
                <h3 style={{ color: '#d32f2f', marginBottom: '1rem' }}>Cancel Appointment</h3>
                <p style={{ color: '#666', marginBottom: '1rem' }}>
                  Are you sure you want to cancel this appointment?
                </p>
                {appointmentToCancel && (
                  <div className="card" style={{ background: '#f5f5f5', textAlign: 'left' }}>
                    <div><strong>Service:</strong> {appointmentToCancel.service}</div>
                    <div><strong>Date:</strong> {appointmentToCancel.date}</div>
                    <div><strong>Time:</strong> {appointmentToCancel.time}</div>
                    {appointmentToCancel.notes && (
                      <div><strong>Notes:</strong> {appointmentToCancel.notes}</div>
                    )}
                  </div>
                )}
                <p style={{ color: '#d32f2f', fontSize: '0.875rem', marginTop: '1rem' }}>
                  This action cannot be undone.
                </p>
              </div>
              <div style={{ display: 'flex', gap: '1rem', justifyContent: 'center' }}>
                <button 
                  className="btn btn-secondary"
                  onClick={closeCancelModal}
                >
                  Keep Appointment
                </button>
                <button 
                  className="btn"
                  style={{ background: '#d32f2f', color: 'white' }}
                  onClick={confirmCancelAppointment}
                >
                  Yes, Cancel Appointment
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Cancel Success Screen */}
        {cancelSuccess && (
          <div style={{ textAlign: 'center', padding: '3rem 1rem' }}>
            <div style={{ 
              fontSize: '4rem', 
              marginBottom: '1rem',
              animation: 'bounce 1s ease-in-out'
            }}>
              ✅
            </div>
            <h2 style={{ 
              color: '#2e7d32', 
              marginBottom: '1rem',
              fontSize: '2rem',
              fontWeight: 'bold'
            }}>
              Appointment Cancelled Successfully
            </h2>
            <div className="card" style={{ 
              maxWidth: '500px', 
              margin: '0 auto 2rem auto',
              background: '#fff3e0',
              border: '2px solid #ff9800'
            }}>
              <h4 style={{ marginBottom: '1rem', color: '#f57c00' }}>Cancelled Appointment</h4>
              {appointmentToCancel && (
                <div style={{ display: 'grid', gap: '0.75rem', textAlign: 'left' }}>
                  <div><strong>Service:</strong> {appointmentToCancel.service}</div>
                  <div><strong>Date:</strong> {appointmentToCancel.date}</div>
                  <div><strong>Time:</strong> {appointmentToCancel.time}</div>
                  {appointmentToCancel.notes && (
                    <div><strong>Notes:</strong> {appointmentToCancel.notes}</div>
                  )}
                  <div><strong>Status:</strong> <span style={{ color: '#d32f2f', fontWeight: '600' }}>Cancelled</span></div>
                </div>
              )}
            </div>
            <p style={{ color: '#666', marginBottom: '2rem' }}>
              Your appointment has been cancelled. You can book a new appointment anytime.
            </p>
            <button 
              className="btn btn-primary"
              onClick={() => {
                setCancelSuccess(false);
                setAppointmentToCancel(null);
              }}
            >
              Book New Appointment
            </button>
            <div style={{ 
              background: '#f0f0f0', 
              padding: '1rem', 
              borderRadius: '8px',
              color: '#666',
              fontSize: '0.875rem',
              marginTop: '2rem'
            }}>
              Returning to appointments in a few seconds...
            </div>
          </div>
        )}

        {/* View Appointment Details Modal */}
        {showViewModal && viewingAppointment && (
          <div className="modern-modal-overlay" onClick={closeViewModal}>
            <div className="modern-modal" onClick={(e) => e.stopPropagation()}>
              <div className="modern-modal-header">
                <h2 className="modern-modal-title">
                  <span className="modern-modal-icon">📋</span>
                  Appointment Details
                </h2>
                <button 
                  className="modern-modal-close"
                  onClick={closeViewModal}
                >
                  ✕
                </button>
              </div>
              
              <div className="modern-modal-body">
                <div className="modern-card">
                  <div className="modern-card-header">
                    <div className="modern-estimate-service">
                      <div className="modern-estimate-icon">
                        {viewingAppointment.service.includes('Tree Removal') ? '🌳' :
                         viewingAppointment.service.includes('Pruning') ? '✂️' :
                         viewingAppointment.service.includes('Trimming') ? '✂️' :
                         viewingAppointment.service.includes('Planting') ? '🌱' :
                         viewingAppointment.service.includes('Emergency') ? '🚨' : '🌿'}
                      </div>
                      <div>
                        <h3 className="modern-estimate-title">{viewingAppointment.service}</h3>
                        <div className="modern-estimate-status">
                          {getStatusBadge(viewingAppointment.status)}
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="modern-form-section">
                    <div className="modern-booking-summary">
                      <div className="modern-booking-detail">
                        <span className="modern-booking-label">📅 Date:</span>
                        <span className="modern-booking-value">{new Date(viewingAppointment.date).toLocaleDateString('en-US', { 
                          weekday: 'long', 
                          year: 'numeric', 
                          month: 'long', 
                          day: 'numeric' 
                        })}</span>
                      </div>
                      
                      <div className="modern-booking-detail">
                        <span className="modern-booking-label">⏰ Time:</span>
                        <span className="modern-booking-value">{viewingAppointment.time}</span>
                      </div>
                      
                      <div className="modern-booking-detail">
                        <span className="modern-booking-label">👤 Customer:</span>
                        <span className="modern-booking-value">{viewingAppointment.userName}</span>
                      </div>
                      
                      <div className="modern-booking-detail">
                        <span className="modern-booking-label">📧 Email:</span>
                        <span className="modern-booking-value">{viewingAppointment.userEmail}</span>
                      </div>
                      
                      {viewingAppointment.notes && (
                        <div className="modern-booking-detail">
                          <span className="modern-booking-label">📝 Notes:</span>
                          <span className="modern-booking-value">{viewingAppointment.notes}</span>
                        </div>
                      )}
                      
                      <div className="modern-booking-detail">
                        <span className="modern-booking-label">📊 Status:</span>
                        <span className="modern-booking-value">
                          <span style={{ 
                            color: getStatusColor(viewingAppointment.status),
                            fontWeight: '600',
                            textTransform: 'capitalize'
                          }}>
                            {viewingAppointment.status}
                          </span>
                        </span>
                      </div>
                      
                      <div className="modern-booking-detail">
                        <span className="modern-booking-label">📅 Booked:</span>
                        <span className="modern-booking-value">
                          {viewingAppointment.createdAt ? 
                            new Date(viewingAppointment.createdAt).toLocaleDateString('en-US', { 
                              year: 'numeric', 
                              month: 'short', 
                              day: 'numeric',
                              hour: '2-digit',
                              minute: '2-digit'
                            }) : 'N/A'
                          }
                        </span>
                      </div>
                    </div>
                  </div>

                  <div className="modern-modal-actions">
                    {canEditOrDelete(viewingAppointment.status) && (
                      <>
                        <button 
                          className="btn btn-secondary"
                          onClick={() => {
                            closeViewModal();
                            handleEditAppointment(viewingAppointment);
                          }}
                        >
                          <span>□</span>
                          Edit Appointment
                        </button>
                        <button 
                          className="btn btn-danger"
                          onClick={() => {
                            closeViewModal();
                            handleDeleteAppointment(viewingAppointment.id);
                          }}
                        >
                          <span>❌</span>
                          Cancel Appointment
                        </button>
                      </>
                    )}
                    {viewingAppointment.status === 'completed' && (
                      <ReviewButton 
                        appointmentId={viewingAppointment.id} 
                        appointmentService={viewingAppointment.service} 
                      />
                    )}
                    <button 
                      className="btn btn-primary"
                      onClick={closeViewModal}
                    >
                      <span>✓</span>
                      Close
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* CSS for alert animation */}
      <style jsx>{`
        @keyframes slideInRight {
          from {
            transform: translateX(400px);
            opacity: 0;
          }
          to {
            transform: translateX(0);
            opacity: 1;
          }
        }
      `}</style>
    </Layout>
  );
};

export default Appointments;